import type { ReportsOverview, ReportsSeriesPoint } from 'shared';
import ChartCard from '@/components/charts/ChartCard';
import ColumnChart, { type ChartSeries } from '@/components/charts/ColumnChart';
import DataTable, { type Column } from '@/components/charts/DataTable';
import { formatCount, formatHours } from '@/lib/reportFormat';
import { BUCKET_LABELS, stepLabel, stepTitle } from '@/lib/reportPeriod';

// A colour belongs to what it measures, wherever on the page it is drawn.
const ENTRIES: ChartSeries = { key: 'visits', label: 'Entradas de participantes', color: 'var(--chart-1)' };
const VISITORS: ChartSeries = { key: 'visitors', label: 'Visitantes em grupo', color: 'var(--chart-2)' };
const EQUIPMENT: ChartSeries = { key: 'hours', label: 'Horas de equipamento', color: 'var(--chart-3)' };

interface ReportChartProps {
  report: ReportsOverview;
}

/** Who came through the door, step by step: participants and visiting groups. */
export function EntriesChart({ report }: ReportChartProps) {
  const { series, bucket, summary } = report;
  const people = summary.visits + summary.visitors;

  const columns: Column<ReportsSeriesPoint>[] = [
    { key: 'step', header: 'Período', cell: (p) => <span className="first-letter:uppercase inline-block">{stepTitle(p.start, bucket)}</span> },
    { key: 'visits', header: 'Entradas', numeric: true, cell: (p) => formatCount(p.visits) },
    { key: 'visitors', header: 'Visitantes em grupo', numeric: true, cell: (p) => formatCount(p.visitors) },
    { key: 'total', header: 'Total', numeric: true, cell: (p) => formatCount(p.visits + p.visitors) },
  ];

  return (
    <ChartCard
      title="Quem passou pelo centro"
      subtitle={`${formatCount(people)} pessoas, ${BUCKET_LABELS[bucket]}`}
      empty={people === 0}
      emptyMessage="Sem entradas nem visitas de grupos no período escolhido."
      chart={
        <ColumnChart
          series={[ENTRIES, VISITORS]}
          steps={series.map((p) => ({
            key: p.start,
            label: stepLabel(p.start, bucket),
            title: stepTitle(p.start, bucket),
            values: [p.visits, p.visitors],
          }))}
          formatValue={formatCount}
          wholeNumbers
          ariaLabel={`Entradas de participantes e visitantes em grupo, ${BUCKET_LABELS[bucket]}. ${formatCount(
            summary.visits,
          )} entradas e ${formatCount(summary.visitors)} visitantes no período.`}
        />
      }
      table={
        <DataTable
          caption={`Entradas e visitantes, ${BUCKET_LABELS[bucket]}`}
          columns={columns}
          rows={series}
          rowKey={(p) => p.start}
          footer={['Total', formatCount(summary.visits), formatCount(summary.visitors), formatCount(people)]}
        />
      }
    />
  );
}

/** How long equipment was in use, step by step. */
export function EquipmentHoursChart({ report }: ReportChartProps) {
  const { series, bucket, summary } = report;

  const columns: Column<ReportsSeriesPoint>[] = [
    { key: 'step', header: 'Período', cell: (p) => <span className="first-letter:uppercase inline-block">{stepTitle(p.start, bucket)}</span> },
    { key: 'hours', header: 'Horas', numeric: true, cell: (p) => formatHours(p.equipmentHours) },
  ];

  return (
    <ChartCard
      title="Horas de equipamento"
      subtitle={`${formatHours(summary.equipmentHours)} em ${formatCount(summary.equipmentSessions)} utilizações, ${BUCKET_LABELS[bucket]}`}
      empty={summary.equipmentSessions === 0}
      emptyMessage="Nenhum equipamento foi usado no período escolhido."
      chart={
        <ColumnChart
          series={[EQUIPMENT]}
          steps={series.map((p) => ({
            key: p.start,
            label: stepLabel(p.start, bucket),
            title: stepTitle(p.start, bucket),
            values: [p.equipmentHours],
          }))}
          formatValue={formatHours}
          ariaLabel={`Horas de utilização de equipamento, ${BUCKET_LABELS[bucket]}. ${formatHours(
            summary.equipmentHours,
          )} no período.`}
        />
      }
      table={
        <DataTable
          caption={`Horas de equipamento, ${BUCKET_LABELS[bucket]}`}
          columns={columns}
          rows={series}
          rowKey={(p) => p.start}
          footer={['Total', formatHours(summary.equipmentHours)]}
        />
      }
    />
  );
}
